import { useCallback, useEffect, useMemo, useState } from 'react';
import cssVars from 'css-vars-ponyfill';
import ReactGA from 'react-ga4';

import { usePostMessageListener } from '@hooks/usePostMessageListener';
import { defaultTheme, ThemeProvider } from '@spazious/react-styles';
import { InitConfigPayload, PostMessageType, ThemeColors } from '@spazious/search-sdk-types';

import { useHotelChainData } from './AppHotelChainDataProvider.js';
export interface AppDynamicThemingProps {
  children?: React.ReactNode;
}

export const AppDynamicTheming: React.FC<AppDynamicThemingProps> = ({ children }) => {
  const hotelChainData = useHotelChainData();
  const chainInfo = hotelChainData?.data?.data?.attributes;

  const [parentColors, setParentColors] = useState<ThemeColors>({});

  usePostMessageListener<InitConfigPayload>(
    PostMessageType.INIT_CONFIG,
    useCallback((payload) => {
      if (payload?.colors) {
        setParentColors(payload.colors);
      }
    }, [])
  );
  const resolvedColors = useMemo(
    () => ({
      primaryColor: parentColors.primaryColor ?? chainInfo?.primaryColor ?? 'black',
      secondaryColor: parentColors.secondaryColor ?? chainInfo?.secondaryColor ?? 'grey',
      accentColor: parentColors.accentColor ?? chainInfo?.secondaryColor ?? 'black',
      backgroundColor: parentColors.backgroundColor,
      backgroundMuted: parentColors.backgroundMuted,
      primaryText: parentColors.primaryText,
      secondaryText: parentColors.secondaryText,
    }),
    [
      parentColors.primaryColor,
      parentColors.secondaryColor,
      parentColors.accentColor,
      parentColors.backgroundColor,
      parentColors.backgroundMuted,
      chainInfo?.primaryColor,
      chainInfo?.secondaryColor,
      parentColors.primaryText,
      parentColors.secondaryText,
    ]
  );

  const hotelChainTheme = useMemo(
    () => ({
      ...defaultTheme,
      palette: {
        ...defaultTheme.palette,
        colors: {
          ...defaultTheme.palette.colors,
          primary: resolvedColors.secondaryColor,
          secondary: resolvedColors.primaryColor,
        },
      },
    }),
    [resolvedColors]
  );

  const gaTagId = chainInfo?.gaTrackingId;
  useEffect(() => {
    if (!!gaTagId) {
      ReactGA.initialize(gaTagId);
    } else {
      ReactGA.reset();
    }
  }, [gaTagId]);

  useEffect(() => {
    const cssVariables: Record<string, string> = {
      'primary-color': resolvedColors.primaryColor,
      'accent-color': resolvedColors.accentColor,
      'secondary-color': resolvedColors.secondaryColor,
    };

    if (resolvedColors.backgroundColor) {
      cssVariables['background-color'] = resolvedColors.backgroundColor;
    }

    if (resolvedColors.backgroundMuted) {
      cssVariables['background-muted'] = resolvedColors.backgroundMuted;
    }
    if (resolvedColors.primaryText) {
      cssVariables['primary-text'] = resolvedColors.primaryText;
    }

    if (resolvedColors.secondaryText) {
      cssVariables['secondary-text'] = resolvedColors.secondaryText;
    }

    cssVars({ variables: cssVariables });
  }, [resolvedColors]);

  return <ThemeProvider theme={hotelChainTheme}>{children}</ThemeProvider>;
};

export default AppDynamicTheming;
